import React, { useState } from "react";
import { Box, Button, Card, CardContent, Typography, LinearProgress, List, ListItem, ListItemText, Alert } from "@mui/material";
import { generateClient } from 'aws-amplify/api';
import { uploadData } from 'aws-amplify/storage';
import { fetchAuthSession } from 'aws-amplify/auth';
import EmailChipsInput from '../../components/EmailChipsInput';

// import { getResource } from 'aws-amplify/storage';

const client = generateClient({authMode: 'userPool'});
const endpoint = "https://q7cf8zbmqf.execute-api.us-west-2.amazonaws.com/staging/refunds";

const allowedExtensions = [".xlsx", ".xls", ".xlsm"];

function isAllowed(file) {
  return allowedExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
}

const RefundSummaries = () => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [processing, setProcessing] = useState(false);
  const [downloadLinks, setDownloadLinks] = useState(null); // { pdf: "...", excel: "..." }
  const [emails, setEmails] = useState([]);

  // Handle file selection
const handleFileChange = (event) => {
  setError("");
  const selected = Array.from(event.target.files);

  // Filter out files with disallowed extensions
  const badFiles = selected.filter(f => !isAllowed(f));
  if (badFiles.length) {
    setError(`Only Excel files (.xlsx, .xls, .xlsm) are allowed.`);
    return;
  }

  // Combine old and new files, filter duplicates by name
  const merged = [...files, ...selected];
  // To prevent duplicate files (by name)
  const unique = Array.from(
    new Map(merged.map(f => [f.name, f])).values()
  );
  setFiles(unique);
};

function validateEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

  // Update for REST API and S3 bucket name details
const handleUpload = async () => {
  setError("");
  setDownloadLinks(null);

  if (files.length < 4 || files.length > 5) {
    setError("Please upload four or five Excel files.");
    return;
  }

  const invalidFile = files.find(f => !f.name.toUpperCase().includes("VALUES"));
    if (invalidFile) {
      setError("Submission failed; please ensure formulas have been converted to values via Excel macro.");
      return;
    }

  if (emails.length === 0 || emails.some(e => !validateEmail(e))) {
  setError("Please provide at least one valid email address.");
  return;
}


  setUploading(true);

  try {
    // 1. Upload files to S3 -- NOTE, uploadData appears as deprecated, but Amplify
    // has not replaced this method with the promised replacement, "getResource".
    // As such, I have preserved uploadData in the code.
    const uploadResults = await Promise.all(
      files.map(async (file) => {
        const s3Key = `uploads/${Date.now()}-${file.name}`;
        await uploadData({ key: s3Key, data: file, options: { contentType: file.type } }).result;
        return s3Key;
      })
    );


    setUploading(false);
    setProcessing(true);

    // 2. Get auth token for API call
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();

    // 3. Call the REST API to process the uploaded files
    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        bucket: "timelossapp05a90dd981aa4ddb9fbb139fd9f7bfba134215-staging",
        fileKeys: uploadResults,
        userEmails: emails,
      }),
    });

        // Check if response is ok
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const result = await response.json();

    setProcessing(false);
    setDownloadLinks({
      pdf: result.pdfDownloadUrl,
      excel: result.excelDownloadUrl,
    });
  } catch (err) {
    setUploading(false);
    setProcessing(false);
    setError("Failed to upload or process files: " + err.message);
  }
};

  return (
    <Box sx={{ maxWidth: 600, margin: "0 auto", mt: 6 }}>
      <Card elevation={3} sx={{ p: 2 }}>
        <CardContent>

          <Typography variant="h5" gutterBottom>
            Upload Adjustment Files
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Please upload values-only refund Excel files (one for each adjustment year, plus the refunds with deductions file).
          </Typography>
          
          <input
            id="excel-upload"
            type="file"
            accept=".xlsx,.xls,.xlsm"
            multiple
            onChange={handleFileChange}
            disabled={uploading || processing}
            style={{ display: "none" }}
          />
          <label htmlFor="excel-upload">
            <Button variant="contained" component="span" disabled={uploading || processing}>
              Choose Files
            </Button>
          </label>
          <List dense>
            {files.map((file, idx) => (
              <ListItem
                key={file.name + idx}
                secondaryAction={
                  <Button
                    size="small"
                    color="error"
                    onClick={() => {
                      setFiles(files.filter((_, i) => i !== idx));
                    }}
                  >
                    Remove
                  </Button>
                }
              >
                <ListItemText primary={file.name} />
              </ListItem>
            ))}
          </List>
          {error && <Alert severity="error" sx={{ my: 1 }}>{error}</Alert>}

           <EmailChipsInput emails={emails} setEmails={setEmails} disabled={uploading || processing} />

          <Button
            variant="contained"
            color="primary"
            sx={{ mt: 2 }}
            disabled={uploading || processing || files.length === 0}
            onClick={handleUpload}
            fullWidth
          >
            {uploading ? "Uploading..." : "Upload and Process"}
          </Button>
          
          {uploading && <LinearProgress sx={{ mt: 2 }} />}
          {processing && (
            <Box sx={{ mt: 2 }}>
              <Typography>Processing files, please wait...</Typography>
              <LinearProgress />
            </Box>
          )}

          {downloadLinks && (
            <Box sx={{ mt: 3 }}>
              <Alert severity="success">Files processed successfully!</Alert>
              {/* <Button
                sx={{ mt: 1, mr: 1 }}
                href={downloadLinks.pdf}
                target="_blank"
                variant="outlined"
                download
              >
                Download PDF
              </Button>
              <Button
                sx={{ mt: 1 }}
                href={downloadLinks.excel}
                target="_blank"
                variant="outlined"
                download
              >
                Download Excel
              </Button> */}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default RefundSummaries;
